'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import ModalShell from '@/components/ui/ModalShell';
import HQLocationContent from '@/components/common/HQLocationContent';
import { useModalState } from '@/hooks/useModalState';
import { ICON_BUTTON_CLASS } from '@/components/common/iconButton';

interface HQMapDialogProps {
  /** GA4 `cta_click` id - Analytics.tsx picks this up by event delegation. */
  analyticsId: string;
  /** Colour treatment for the surrounding surface. Sizing is handled here. */
  className?: string;
  size?: number;
}

const TITLE_ID = 'hq-map-dialog-title';

/**
 * A map button that opens the Eco-HQ panel in a popup.
 *
 * Sits beside the address row as a sibling of the "open in Google Maps" link,
 * never inside it - a button nested in an anchor is invalid HTML, the same trap
 * the WhatsApp buttons avoid (see WhatsAppLink).
 *
 * The dialog subtree only mounts while open, so the Google Maps iframe inside
 * HQLocationContent is not requested until someone asks for it. That matters in
 * the footer, which renders on every page.
 */
export default function HQMapDialog({
  analyticsId,
  className = '',
  size = 18,
}: Readonly<HQMapDialogProps>) {
  const [open, setOpen] = useState(false);
  // useModalState drives the enter/exit transition from a nullable value.
  const { render, visible } = useModalState(open ? 'open' : null);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-analytics-id={analyticsId}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="View our HQ location, office hours and directions"
        className={`${ICON_BUTTON_CLASS} ${className}`}
      >
        <Icon name="MapIcon" size={size} aria-hidden="true" />
      </button>

      {render && (
        <ModalShell
          visible={visible}
          onClose={() => setOpen(false)}
          labelledBy={TITLE_ID}
          closeLabel="Close the HQ location popup"
          dialogClassName="max-w-3xl rounded-2xl"
        >
          <div className="p-4 lg:p-6">
            {/* pr-12 keeps the heading clear of the shell's 44px close button. */}
            <h2
              id={TITLE_ID}
              className="mb-4 pr-12 font-headline text-2xl font-bold text-foreground"
            >
              Visit Our Eco-HQ
            </h2>
            <HQLocationContent className="rounded-2xl" />
          </div>
        </ModalShell>
      )}
    </>
  );
}
